class BAOCollectionFilters extends window.BAO.CustomElement() {
  setupListeners () {
    this.filtersSectionId = 'bao-collection-filters'
    this.controller = null
    this.pendingSearch = null
    this.currentSearch = this.normalise(window.location.search)

    this.listeners.add(this, 'change', this.onChange.bind(this))
    this.listeners.add(this, 'click', this.onClick.bind(this))
    this.listeners.add(this, 'submit', this.onSubmit.bind(this))
    this.listeners.add(this, 'keydown', this.onKeydown.bind(this))
    this.listeners.add(this, 'focusout', this.onFocusOut.bind(this))
    this.listeners.add(window, 'popstate', this.onPopState.bind(this))
  }

  get sectionId () {
    return document.getElementById('product-grid')?.dataset.id
  }

  normalise (search) {
    const params = new URLSearchParams()

    new URLSearchParams(search).forEach((value, key) => {
      if (key === 'page' || value === '') return

      params.append(key, value)
    })

    return params.toString()
  }

  formSearch () {
    const params = new URLSearchParams()

    const defaultSort = this.els.form.element.elements.sort_by?.dataset.default

    new FormData(this.els.form.element).forEach((value, key) => {
      if (key === 'sort_by' && value === defaultSort) return

      params.append(key, value)
    })

    return this.normalise(params.toString())
  }

  onChange (event) {
    const input = event.target

    if (!input.matches('input, select')) return

    if (input.type === 'number') return

    this.render(this.formSearch(), input.id)
  }

  onFocusOut (event) {
    const input = event.target

    if (input.type !== 'number') return

    this.orderPrices()

    this.render(this.formSearch(), input.id)
  }

  onClick (event) {
    const link = event.target.closest('[data-bao-collection-filters-el="link"]')

    if (!link) return

    event.preventDefault()

    this.render(this.normalise(new URL(link.href).search), null)
  }

  onKeydown (event) {
    const input = event.target

    if (event.key !== 'Enter' || input.type !== 'number') return

    event.preventDefault()

    this.orderPrices()

    this.render(this.formSearch(), input.id)
  }

  onSubmit (event) {
    event.preventDefault()

    this.orderPrices()

    this.render(this.formSearch(), document.activeElement?.id)
  }

  onPopState () {
    const search = this.normalise(window.location.search)

    if (search === this.currentSearch) return

    this.render(search, undefined, false)
  }

  orderPrices () {
    const inputs = Array.from(
      this.els.form.element.querySelectorAll('input[type="number"]'),
    )

    if (inputs.length !== 2) return

    const [min, max] = inputs

    if (min.value === '' || max.value === '') return
    if (Number(min.value) <= Number(max.value)) return

    const minValue = min.value

    min.value = max.value
    max.value = minValue
  }

  render (search, focusId, updateHistory = true) {
    if (search === this.pendingSearch) return
    if (this.pendingSearch === null && search === this.currentSearch) return

    const url = `${window.location.pathname}${search ? `?${search}` : ''}`
    const results = this.externalEls.results?.element

    if (!this.sectionId || !results) {
      window.location.href = url
      return
    }

    this.controller?.abort()
    this.controller = new AbortController()
    this.pendingSearch = search

    results.setAttribute('aria-busy', 'true')

    const params = new URLSearchParams(search)

    params.set('sections', `${this.sectionId},${this.filtersSectionId}`)

    fetch(`${window.location.pathname}?${params}`, {
      signal: this.controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText)

        return response.json()
      })
      .then((sections) => {
        this.update(sections, focusId)

        if (updateHistory) {
          window.history.pushState({ search }, '', url)
        }

        this.currentSearch = search
        this.pendingSearch = null
        this.controller = null

        this.externalEls.results?.element?.removeAttribute('aria-busy')
      })
      .catch((error) => {
        if (error.name === 'AbortError') return

        window.location.href = url
      })
  }

  update (sections, focusId) {
    const parser = new DOMParser()
    const gridDoc = parser.parseFromString(sections[this.sectionId], 'text/html')
    const filtersDoc = parser.parseFromString(
      sections[this.filtersSectionId],
      'text/html',
    )

    const results = this.externalEls.results?.element
    const count = this.externalEls.count?.element
    const body = this.els.body?.element

    const newResults = gridDoc.querySelector(
      '[data-bao-collection-filters-external-el="results"]',
    )
    const newCount = gridDoc.querySelector(
      '[data-bao-collection-filters-external-el="count"]',
    )
    const newBody = filtersDoc.querySelector(
      '[data-bao-collection-filters-el="body"]',
    )

    if (!results || !body || !newResults || !newBody) {
      throw new Error('Missing filter regions')
    }

    const openState = this.captureOpenState()
    const active = document.activeElement
    const moved =
      this.contains(active) && active.id && active.id !== focusId
        ? { id: active.id, value: active.value }
        : null

    results.innerHTML = newResults.innerHTML
    body.innerHTML = newBody.innerHTML

    if (count && newCount) count.textContent = newCount.textContent

    this.restoreOpenState(openState)

    if (moved) {
      this.restoreMovedFocus(moved)
    } else if (focusId !== undefined) {
      this.restoreFocus(focusId)
    }

    this.announce(newCount?.textContent)

    window.gridLayoutSwitcher?.syncGrid?.()

    if (typeof window.initializeScrollAnimationTrigger === 'function') {
      window.initializeScrollAnimationTrigger(results)
    }
  }

  captureOpenState () {
    return (this.els.group?.elements || []).reduce((state, group) => {
      state[group.dataset.key] = group.open

      return state
    }, {})
  }

  restoreOpenState (state) {
    const groups = this.els.group?.elements || []

    groups.forEach((group) => {
      if (group.dataset.key in state) group.open = state[group.dataset.key]
    })
  }

  restoreFocus (focusId) {
    const active = document.activeElement

    if (active && active !== document.body && !this.contains(active)) return

    const input = focusId ? document.getElementById(focusId) : null
    const target =
      input && !input.disabled
        ? input
        : document.getElementById('CollectionFiltersTitle')

    target?.focus({ preventScroll: true })
  }

  restoreMovedFocus ({ id, value }) {
    const el = document.getElementById(id)

    if (!el) return

    if (el.type === 'number') el.value = value

    el.focus({ preventScroll: true })
  }

  announce (text) {
    const status = this.els.status?.element

    if (!status || !text) return

    status.textContent = ''

    window.requestAnimationFrame(() => {
      status.textContent = text.trim()
    })
  }
}

if (!customElements.get('bao-collection-filters')) {
  customElements.define('bao-collection-filters', BAOCollectionFilters)
}
